import { useRef, useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ArrowDown } from "lucide-react";
import { TERMS, PRIVACY, LEGAL_VERSION } from "@/content/legal";

export const LegalText = ({ sections }) => (
  <div className="space-y-4">
    {sections.map(([h, t]) => (
      <div key={h}>
        <h3 className="font-semibold text-sm text-[#172238]">{h}</h3>
        <p className="text-[13px] leading-relaxed text-[#5A6B87] mt-1">{t}</p>
      </div>
    ))}
  </div>
);

export function LegalModal({ open, kind, onAccept, onClose }) {
  const ref = useRef(null);
  const [atEnd, setAtEnd] = useState(false);
  const [checked, setChecked] = useState(false);
  const isTerms = kind === "terms";
  useEffect(() => { setAtEnd(false); setChecked(false); if (ref.current) ref.current.scrollTop = 0; }, [open, kind]);
  const onScroll = (e) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 8) setAtEnd(true);
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0" data-testid={`legal-modal-${kind}`}>
        <DialogHeader className="p-6 pb-3 border-b">
          <DialogTitle className="font-display">{isTerms ? "Terms & Conditions" : "Privacy Policy"}</DialogTitle>
          <DialogDescription>Nirogyam Diagnostics and Healthcare Pvt Ltd · {LEGAL_VERSION}. Please scroll to the end to continue.</DialogDescription>
        </DialogHeader>
        <div ref={ref} onScroll={onScroll} className="max-h-[52vh] overflow-y-auto px-6 py-4" data-testid={`legal-scroll-${kind}`}>
          <LegalText sections={isTerms ? TERMS : PRIVACY} />
          <div className="mt-6 text-xs text-center text-[#2E9B65] font-medium">— End of document —</div>
        </div>
        <div className="border-t p-5 space-y-3 bg-[#F4F6F8] rounded-b-lg">
          {!atEnd && (
            <div className="flex items-center gap-2 text-xs text-amber-700" data-testid="legal-scroll-hint">
              <ArrowDown className="h-3.5 w-3.5 animate-bounce" /> Scroll down to the end to enable acceptance
            </div>
          )}
          <label className={`flex items-start gap-2.5 text-sm ${atEnd ? "" : "opacity-50"}`}>
            <Checkbox disabled={!atEnd} checked={checked} onCheckedChange={(v) => setChecked(!!v)} data-testid={`legal-accept-checkbox-${kind}`} />
            I have read and agree to the {isTerms ? "Terms & Conditions" : "Privacy Policy"} of Nirogyam Diagnostics and Healthcare Pvt Ltd.
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose} data-testid={`legal-cancel-${kind}`}>Cancel</Button>
            <Button disabled={!atEnd || !checked} onClick={onAccept} className="bg-[#48217A] hover:bg-[#3B1A66]" data-testid={`legal-accept-btn-${kind}`}>
              Accept & continue
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
